/**
 * HouseholdCaptureWidget
 *
 * TelePortal Setup step that captures who is relocating with the talent.
 * Collects per-member minimum data: name, nationality, hasPassport, vaccinationOk.
 * Payload shape matches SetupProfile.household so the instantiation engine can
 * expand per_member / per_child / per_pet tasks for each person.
 */

import { useCallback, useEffect, useId, useMemo, useRef } from 'react';
import { useWidgetState } from '@/features/platform/copilot/flow-lifecycle';
import { useJourneyInstance } from '@/core/contexts/JourneyInstanceContext';
import type { WidgetProps } from '@tonnie/copilot';
import { useTranslation, useCountries } from '@tonnie/locale';
import { splitDisplayName } from '@tonnie/foundation';
import { useTeleportalHouseholdProposal } from '@tonnie/sdk';
import {
  DEFAULT_PET_SPECIES,
  seedHouseholdDraft,
  type HouseholdDraft,
  type HouseholdDraftMember,
} from './household-draft';
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  CountrySelect,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from '@tonnie/ui';
import { CheckCircle2, Heart, Baby, Dog, Plus, Trash2, Users, ArrowRight } from '@tonnie/assets';

// ── Types ──

interface HouseholdCaptureConfig {
  title?: string;
  description?: string;
  allowPartner?: boolean;
  allowChildren?: boolean;
  allowPets?: boolean;
  collectDetails?: boolean;
}

interface HouseholdPayload {
  household: {
    has_partner: boolean;
    has_children: boolean;
    has_pets: boolean;
    members: ReturnType<typeof toWireMember>[];
  };
}

// ── Helpers ──

let _idCounter = 0;
function nextId(prefix: string): string {
  return `${prefix}_${++_idCounter}_${Date.now()}`;
}

/** The wire shape: a stable ref plus STRUCTURED first/last, never one string. */
function toWireMember(m: HouseholdDraftMember) {
  const { firstName, lastName } = splitDisplayName(m.name);
  return {
    ref: m.id,
    id: m.id,
    type: m.type,
    name: m.name,
    firstName,
    lastName,
    nationality: m.nationality,
    hasPassport: m.hasPassport,
    vaccinationOk: m.vaccinationOk,
    ...(m.type === 'pet' ? { species: m.species, petSpecies: m.species } : {}),
  };
}

// ── Component ──

export function HouseholdCaptureWidget({ config: _cfg, onComplete }: WidgetProps) {
  const { t } = useTranslation();
  const cfg = (_cfg ?? {}) as HouseholdCaptureConfig;
  const allowPartner = cfg.allowPartner !== false;
  const allowChildren = cfg.allowChildren !== false;
  const allowPets = cfg.allowPets !== false;

  // The whole household form is real user work — partner details,
  // each child, each pet. Persist as one slice so navigating away
  // mid-input doesn't blow away the family they just typed in.
  const defaultPartner: HouseholdDraftMember = {
    id: nextId('partner'),
    type: 'partner',
    name: '',
    nationality: '',
  };
  const { activeInstance: journey } = useJourneyInstance();
  const journeyId = journey?.id ?? '';
  // The server's ONE prefill chain (F35 · D-17): the saved roster — so
  // re-entering setup edits the SAME people rather than minting a second copy —
  // else the citizen's onboarding family answer, PROPOSED and saved only when
  // they confirm this step, else nothing.
  const { data: proposal } = useTeleportalHouseholdProposal(journeyId);

  const [persisted, patchPersisted] = useWidgetState<HouseholdDraft>('household_capture', {
    hasPartner: false,
    hasChildren: false,
    hasPets: false,
    partner: defaultPartner,
    children: [],
    pets: [],
    prefillSource: null,
  });
  const { hasPartner, hasChildren, hasPets, partner, children, pets, prefillSource } = persisted;
  // ALL mutators below MUST go through `patchPersisted`'s functional
  // form (added 2026-04-28). The previous wrapper setters captured
  // `persisted.children` etc. in their closures and forwarded the
  // captured value into the user's updater. Combined with the
  // `useCallback(..., [])` empty-deps on `addChild`/`updateChild`/etc.,
  // every keystroke read a stale `persisted.children=[]` and replaced
  // the live state with `[]`, deleting the entry mid-typing — the
  // "typing blocks" bug. Reading `prev` from inside the patch updater
  // gets the latest local state directly from React's setter.
  // Hydration runs once per mount, and the draft itself records that it was
  // seeded — the rule for how a proposal meets a draft the citizen already
  // touched is `seedHouseholdDraft`'s, not this effect's.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current || !proposal) return;
    hydratedRef.current = true;
    patchPersisted((prev) => seedHouseholdDraft(prev, proposal, nextId) ?? {});
  }, [proposal, patchPersisted]);

  const setHasPartner = useCallback(
    (v: boolean) => patchPersisted({ hasPartner: v }),
    [patchPersisted]
  );
  const setHasChildren = useCallback(
    (v: boolean) => patchPersisted({ hasChildren: v }),
    [patchPersisted]
  );
  const setHasPets = useCallback((v: boolean) => patchPersisted({ hasPets: v }), [patchPersisted]);

  const updatePartner = useCallback(
    (patch: Partial<HouseholdDraftMember>) =>
      patchPersisted((prev) => ({ partner: { ...prev.partner, ...patch } })),
    [patchPersisted]
  );

  const addChild = useCallback(() => {
    patchPersisted((prev) => ({
      children: [
        ...prev.children,
        {
          id: nextId('child'),
          type: 'child',
          name: '',
          nationality: '',
        },
      ],
    }));
  }, [patchPersisted]);

  const removeChild = useCallback(
    (id: string) =>
      patchPersisted((prev) => ({ children: prev.children.filter((c) => c.id !== id) })),
    [patchPersisted]
  );

  const updateChild = useCallback(
    (id: string, patch: Partial<HouseholdDraftMember>) =>
      patchPersisted((prev) => ({
        children: prev.children.map((c) => (c.id === id ? { ...c, ...patch } : c)),
      })),
    [patchPersisted]
  );

  const addPet = useCallback(() => {
    patchPersisted((prev) => ({
      pets: [
        ...prev.pets,
        {
          id: nextId('pet'),
          type: 'pet',
          name: '',
          species: DEFAULT_PET_SPECIES,
        },
      ],
    }));
  }, [patchPersisted]);

  const removePet = useCallback(
    (id: string) => patchPersisted((prev) => ({ pets: prev.pets.filter((p) => p.id !== id) })),
    [patchPersisted]
  );

  const updatePet = useCallback(
    (id: string, patch: Partial<HouseholdDraftMember>) =>
      patchPersisted((prev) => ({
        pets: prev.pets.map((p) => (p.id === id ? { ...p, ...patch } : p)),
      })),
    [patchPersisted]
  );

  const members = useMemo<HouseholdDraftMember[]>(() => {
    const list: HouseholdDraftMember[] = [];
    if (hasPartner) list.push(partner);
    if (hasChildren) list.push(...children);
    if (hasPets) list.push(...pets);
    return list;
  }, [hasPartner, partner, hasChildren, children, hasPets, pets]);

  const summaryParts = useMemo(() => {
    const parts = [t('copilot.household.you')];
    if (hasPartner && partner.name)
      parts.push(`${partner.name} (${t('copilot.household.partner')})`);
    else if (hasPartner) parts.push(t('copilot.household.partner'));
    children.forEach((c) => {
      if (c.name) parts.push(`${c.name} (${t('copilot.household.child')})`);
      else parts.push(t('copilot.household.child'));
    });
    pets.forEach((p) => {
      if (p.name) parts.push(`${p.name} (${p.species ?? t('copilot.household.pet')})`);
      else parts.push(p.species ?? t('copilot.household.pet'));
    });
    return parts;
  }, [hasPartner, partner, children, pets, t]);

  const handleConfirm = useCallback(() => {
    const payload: HouseholdPayload = {
      household: {
        has_partner: hasPartner,
        has_children: hasChildren && children.length > 0,
        has_pets: hasPets && pets.length > 0,
        // The applicant is NOT in this list and never was: the server adds
        // their own roster row from their account, so a client that cannot be
        // trusted with an identity never has to mint one.
        members: members.map(toWireMember),
      },
    };
    onComplete?.(payload);
  }, [hasPartner, hasChildren, hasPets, children, pets, members, onComplete]);

  const title = cfg.title || t('copilot.household.title');
  const description = cfg.description || t('copilot.household.description');

  return (
    <div className="w-full space-y-4 py-2" data-testid="widget-household_capture">
      {/* Header */}
      <div>
        <h3 className="text-base font-semibold flex items-center gap-2">
          <Users className="w-5 h-5 text-primary-accessible" />
          {title}
        </h3>
        <p className="text-sm text-muted-foreground mt-1">{description}</p>
      </div>

      {/* Said out loud, so a pre-filled answer is never mistaken for one the
          citizen typed here: it came from onboarding and nothing is saved
          until they confirm. */}
      {prefillSource === 'onboarding' && (
        <p
          className="text-xs text-muted-foreground"
          data-testid="widget-household_capture-prefill-note"
        >
          {t('copilot.household.prefilledFromOnboarding')}
        </p>
      )}

      {/* Toggle buttons */}
      <div
        className="grid grid-cols-3 gap-2"
        data-testid="widget-household_capture-toggles"
        data-prefill-source={prefillSource ?? 'pending'}
      >
        {allowPartner && (
          <ToggleCard
            active={hasPartner}
            onClick={() => setHasPartner(!hasPartner)}
            icon={<Heart className="w-5 h-5" />}
            label={t('copilot.household.partner')}
            testId="widget-household_capture-toggle-partner"
          />
        )}
        {allowChildren && (
          <ToggleCard
            active={hasChildren}
            onClick={() => setHasChildren(!hasChildren)}
            icon={<Baby className="w-5 h-5" />}
            label={t('copilot.household.children')}
            testId="widget-household_capture-toggle-children"
          />
        )}
        {allowPets && (
          <ToggleCard
            active={hasPets}
            onClick={() => setHasPets(!hasPets)}
            icon={<Dog className="w-5 h-5" />}
            label={t('copilot.household.pets')}
            testId="widget-household_capture-toggle-pets"
          />
        )}
      </div>

      {/* Partner section */}
      {hasPartner && (
        <MemberFormCard
          title={t('copilot.household.partnerDetails')}
          icon={<Heart className="w-4 h-4 text-primary-accessible" />}
        >
          <PersonFields
            member={partner}
            onChange={(patch) => updatePartner(patch)}
            testIdPrefix="widget-household_capture-partner"
          />
        </MemberFormCard>
      )}

      {/* Children section */}
      {hasChildren && (
        <MemberFormCard
          title={t('copilot.household.childrenDetails')}
          icon={<Baby className="w-4 h-4 text-primary-accessible" />}
        >
          <div className="space-y-3">
            {children.map((child, idx) => (
              <div
                key={child.id}
                className="relative"
                data-testid={`widget-household_capture-member-${idx}`}
              >
                <p className="text-xs font-medium text-muted-foreground mb-1.5">
                  {t('copilot.household.childN', { n: idx + 1 })}
                </p>
                <PersonFields
                  member={child}
                  onChange={(patch) => updateChild(child.id, patch)}
                  testIdPrefix={`widget-household_capture-member-${idx}`}
                />
                <button
                  type="button"
                  onClick={() => removeChild(child.id)}
                  className="absolute top-0 right-0 p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive-accessible transition-colors"
                  aria-label={t('common.remove')}
                  data-testid={`widget-household_capture-remove-${idx}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            <Button
              variant="outline"
              size="sm"
              onClick={addChild}
              className="gap-1.5"
              data-testid="widget-household_capture-add-member"
            >
              <Plus className="w-3.5 h-3.5" />
              {t('copilot.household.addChild')}
            </Button>
          </div>
        </MemberFormCard>
      )}

      {/* Pets section */}
      {hasPets && (
        <MemberFormCard
          title={t('copilot.household.petsDetails')}
          icon={<Dog className="w-4 h-4 text-primary-accessible" />}
        >
          <div className="space-y-3">
            {pets.map((pet, idx) => (
              <div key={pet.id} className="relative">
                <p className="text-xs font-medium text-muted-foreground mb-1.5">
                  {t('copilot.household.petN', { n: idx + 1 })}
                </p>
                <PetFields member={pet} onChange={(patch) => updatePet(pet.id, patch)} />
                <button
                  type="button"
                  onClick={() => removePet(pet.id)}
                  className="absolute top-0 right-0 p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive-accessible transition-colors"
                  aria-label={t('common.remove')}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addPet} className="gap-1.5">
              <Plus className="w-3.5 h-3.5" />
              {t('copilot.household.addPet')}
            </Button>
          </div>
        </MemberFormCard>
      )}

      {/* Summary strip */}
      {members.length > 0 && (
        <div className="flex items-center gap-2 px-3 py-2 bg-primary/5 rounded-lg border border-primary/20 text-sm">
          <CheckCircle2 className="w-4 h-4 text-primary-accessible shrink-0" />
          <span className="font-medium">
            {t('copilot.household.relocating')} {summaryParts.join(' + ')}
          </span>
        </div>
      )}

      {/* Confirm */}
      <div className="flex justify-end pt-1">
        <Button
          size="sm"
          onClick={handleConfirm}
          className="gap-1.5"
          data-testid="widget-household_capture-confirm"
        >
          <CheckCircle2 className="w-4 h-4" />
          {t('common.confirm')}
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

// ── Sub-components ──

function ToggleCard({
  active,
  onClick,
  icon,
  label,
  testId,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  testId: string;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 transition-all text-sm font-medium',
        active
          ? 'border-primary bg-primary/5 text-primary-accessible shadow-sm'
          : 'border-border hover:border-primary/30 text-muted-foreground'
      )}
    >
      {icon}
      {label}
      {active && <CheckCircle2 className="w-3.5 h-3.5" />}
    </button>
  );
}

function MemberFormCard({
  title,
  icon,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Card className="border-border/50">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
            {icon}
          </div>
          <p className="text-sm font-semibold">{title}</p>
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

function PersonFields({
  member,
  onChange,
  testIdPrefix,
}: {
  member: HouseholdDraftMember;
  onChange: (patch: Partial<HouseholdDraftMember>) => void;
  testIdPrefix: string;
}) {
  const { t } = useTranslation();
  // The picker's countries, named in the reader's language (F34).
  const { countries } = useCountries();
  // One block per household member, so the field ids are per-instance.
  const idBase = useId();
  return (
    <div className="grid grid-cols-2 gap-2">
      <div className="col-span-2 sm:col-span-1">
        <Label htmlFor={`${idBase}-name`} className="text-xs">
          {t('copilot.household.name')}
        </Label>
        <Input
          id={`${idBase}-name`}
          data-testid={`${testIdPrefix}-name`}
          value={member.name}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder={t('copilot.household.namePlaceholder')}
          className="h-8 text-sm"
        />
      </div>
      <div className="col-span-2 sm:col-span-1">
        <Label htmlFor={`${idBase}-nationality`} className="text-xs">
          {t('copilot.household.nationality')}
        </Label>
        <CountrySelect
          id={`${idBase}-nationality`}
          countries={countries}
          value={member.nationality ?? ''}
          onValueChange={(v) => onChange({ nationality: v })}
          placeholder={t('copilot.household.selectCountry')}
          searchPlaceholder={t('copilot.searchCountry')}
          emptyMessage={t('common.noCountryFound')}
          className="h-8 text-sm w-full"
        />
      </div>
      <div className="flex items-center gap-2 col-span-2 sm:col-span-1">
        <Checkbox
          id={`${idBase}-has-passport`}
          data-testid={`${testIdPrefix}-has-passport`}
          checked={member.hasPassport ?? false}
          onCheckedChange={(v) => onChange({ hasPassport: v === true })}
        />
        <Label htmlFor={`${idBase}-has-passport`} className="text-xs cursor-pointer">
          {t('copilot.household.hasPassport')}
        </Label>
      </div>
      <div className="flex items-center gap-2 col-span-2 sm:col-span-1">
        <Checkbox
          id={`${idBase}-vaccination-ok`}
          data-testid={`${testIdPrefix}-vaccination-ok`}
          checked={member.vaccinationOk ?? false}
          onCheckedChange={(v) => onChange({ vaccinationOk: v === true })}
        />
        <Label htmlFor={`${idBase}-vaccination-ok`} className="text-xs cursor-pointer">
          {t('copilot.household.vaccinationOk')}
        </Label>
      </div>
    </div>
  );
}

function PetFields({
  member,
  onChange,
}: {
  member: HouseholdDraftMember;
  onChange: (patch: Partial<HouseholdDraftMember>) => void;
}) {
  const { t } = useTranslation();
  // One block per pet, so the field ids are per-instance.
  const idBase = useId();
  return (
    <div className="grid grid-cols-2 gap-2">
      <div>
        <Label htmlFor={`${idBase}-name`} className="text-xs">
          {t('copilot.household.petName')}
        </Label>
        <Input
          id={`${idBase}-name`}
          value={member.name}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder={t('copilot.household.petNamePlaceholder')}
          className="h-8 text-sm"
        />
      </div>
      <div>
        <Label htmlFor={`${idBase}-species`} className="text-xs">
          {t('copilot.household.species')}
        </Label>
        <Select value={member.species ?? 'dog'} onValueChange={(v) => onChange({ species: v })}>
          <SelectTrigger id={`${idBase}-species`} className="h-8 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="dog">{t('copilot.household.dog')}</SelectItem>
            <SelectItem value="cat">{t('copilot.household.cat')}</SelectItem>
            <SelectItem value="other">{t('copilot.household.other')}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center gap-2 col-span-2">
        <Checkbox
          id={`${idBase}-vaccination-ok`}
          checked={member.vaccinationOk ?? false}
          onCheckedChange={(v) => onChange({ vaccinationOk: v === true })}
        />
        <Label htmlFor={`${idBase}-vaccination-ok`} className="text-xs cursor-pointer">
          {t('copilot.household.petVaccination')}
        </Label>
      </div>
    </div>
  );
}
